import React from 'react';
import { CSSObject } from '@emotion/react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import TextLink from '@cvent/carina/components/TextLink';
import { injectTestId } from '@cvent/nucleus-test-automation';
import useBreakpoints from '@hooks/useBreakpoints';
import useTheme from '@hooks/useTheme';
import { GO_BACK_KEYS, setSessionStorageData } from '@tools/goBack';
import { AUTO_SCROLL_KEYS } from '@tools/goBackScroll';
import { RequiredExceptFor } from '@tools/typeUtilities';
import { UrlObject } from 'url';
import Typography from '@cvent/blocks/components/Typography';
import { SessionCardProps } from './SessionCardType';

interface AgendaItemCardNameProps
  extends RequiredExceptFor<
    Pick<SessionCardProps, 'eventId' | 'id' | 'name' | 'goBackName' | 'goBackSourceType' | 'type'>,
    'goBackName' | 'goBackSourceType' | 'type'
  > {
  children?: JSX.Element;
  goBackTab?: string;
  upgrade?: boolean;
  sessionCardV2?: boolean;
  smallerFontandSpacing?: boolean;
  smallLayout?: boolean;
  onClose?: () => void;
  titleStyleAs?: 'h3' | 'h4' | 'p';
  titleAs?: 'h3' | 'h4' | 'div';
  titleStyle?: CSSObject;
}

const useStyles = (
  sessionCardV2: AgendaItemCardNameProps['sessionCardV2'],
  smallerFontandSpacing: boolean,
  smallLayout
): Record<string, CSSObject> => {
  const { isXL: isBreakpointXL } = useBreakpoints();
  const isXL = isBreakpointXL && !smallLayout;
  const { font } = useTheme();

  const v2NameSize = isXL ? font.base.size.h2 : font.base.size.h3;
  const nameFontSize = sessionCardV2 ? v2NameSize : font.base.size.l;
  return {
    titleContainer: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'flex-start',
      marginTop: sessionCardV2 || smallerFontandSpacing ? 0 : 8,
      wordBreak: 'normal',
      overflowWrap: 'anywhere',
      hyphens: 'auto',
      fontSize: smallerFontandSpacing ? font.base.size[4] : nameFontSize,
      lineHeight: sessionCardV2 || smallerFontandSpacing ? 1.1 : 'inherit',
      paddingBottom: smallerFontandSpacing ? '8px' : null,
      fontWeight: smallerFontandSpacing ? font.base.weight.bold : null,
      // legacy hyphenate limit for Safari
      WebkitHyphenateLimitBefore: '3',
      WebkitHyphenateLimitAfter: '4'
    },
    title: {
      fontWeight: font.base.weight.bold,
      // Line height copied because Link stops styles from reaching the TextLink
      lineHeight: sessionCardV2 || smallerFontandSpacing ? 1.1 : 'inherit',
      // Prevent Live pill from being underlined on title hover
      '& span': {
        textDecoration: 'none',
        display: 'inline-block'
      }
    }
  };
};

function AgendaItemCardName({
  eventId,
  id,
  name,
  type,
  children,
  goBackName,
  goBackTab,
  goBackSourceType,
  upgrade,
  sessionCardV2,
  smallerFontandSpacing,
  smallLayout = false,
  titleStyleAs = 'h3',
  titleAs = 'h3',
  titleStyle,
  onClose
}: AgendaItemCardNameProps): JSX.Element {
  const styles = useStyles(sessionCardV2, smallerFontandSpacing, smallLayout);
  const router = useRouter();

  const { GO_BACK_HREF, GO_BACK_NAME, GO_BACK_SOURCE_TYPE } = GO_BACK_KEYS;
  const { SCROLL_TAB } = AUTO_SCROLL_KEYS;

  const addGoBackData = () => {
    setSessionStorageData({
      [GO_BACK_HREF]: router.asPath,
      [GO_BACK_NAME]: goBackName,
      [GO_BACK_SOURCE_TYPE]: goBackSourceType,
      [SCROLL_TAB]: goBackTab
    });
  };

  const getHref = (itemType: AgendaItemCardNameProps['type']): string | UrlObject => {
    switch (itemType) {
      case 'APPOINTMENT':
        return `/events/${eventId}/schedule?apptId=${id}`;
      case 'PERSONAL_TIME':
        return `/events/${eventId}/schedule?personalTimeId=${id}`;
      default:
        return {
          pathname: `/events/${eventId}/sessions/${id}`,
          query: {
            ...(upgrade && { upgrade: true })
          }
        };
    }
  };

  return (
    <div css={styles.titleContainer}>
      <Link href={getHref(type)} passHref legacyBehavior>
        <TextLink
          {...injectTestId('session-card-title')}
          css={[styles.title]}
          onClick={() => {
            addGoBackData();
            if (onClose) {
              onClose();
            }
          }}
        >
          <Typography as={titleAs} styledAs={titleStyleAs} css={{ margin: 0, ...titleStyle }}>
            {name}
          </Typography>
        </TextLink>
      </Link>
      {children}
    </div>
  );
}

export default AgendaItemCardName;
